import React from 'react';
import { FaShieldAlt } from 'react-icons/fa';

/**
 * SecurityTooltip component displays connection security and encryption details.
 * @param {Object} props
 * @param {boolean} props.visible - Whether the tooltip is visible
 * @param {string} props.serverUrl - The server URL
 * @param {Function} props.onClose - Callback when tooltip should close
 * @param {boolean} props.connected - Socket connection status
 * @param {string} props.protocolState - Current protocol state
 * @param {string} props.roomId - Current room ID
 * @param {Array<string>} props.participants - List of participant names
 * @param {boolean} props.keyEstablished - Whether encryption key is established
 * @param {Object} props.messageCounters - Message counters {sent, received}
 */
const SecurityTooltip = ({
  visible,
  serverUrl,
  onClose,
  connected,
  protocolState,
  roomId,
  participants,
  keyEstablished,
  messageCounters
}) => {
  if (!visible) return null;

  const getProtocolStatusText = () => {
    switch (protocolState) {
      case 'complete': return 'Session Key Derived';
      case 'kc1_sent': case 'kc_wait': return 'Key Confirmation';
      case 'round2_sent': case 'round2_wait': return 'Computing Shared Secret';
      case 'round1_sent': return 'Exchanging Public Values';
      case 'ready': return 'Protocol Ready';
      default: return 'Not Established';
    }
  };

  const getEncryptionDetails = () => {
    if (!keyEstablished) return null;
    return {
      cipher: 'AES-256-GCM',
      keyDerivation: 'HKDF-SHA256',
      keyExchange: 'J-PAKE (RFC 8236)',
      keyConfirmation: 'HMAC-SHA256',
      messagesSent: messageCounters.sent,
      messagesReceived: messageCounters.received,
      aadBinding: 'Counter + Context'
    };
  };

  const encryptionDetails = getEncryptionDetails();

  return (
    <div className="security-tooltip-overlay" onClick={onClose}>
      <div className="security-tooltip" onClick={(e) => e.stopPropagation()}>
        <div className="security-tooltip-header">
          <FaShieldAlt className="security-tooltip-icon" />
          <h3>Connection Security Details</h3>
        </div>

        <div className="security-tooltip-content">
          <div className="security-detail">
            <label>Server Connection:</label>
            <span className="security-value">{serverUrl || 'Unknown'}</span>
          </div>

          <div className="security-detail">
            <label>Connection Status:</label>
            <span className={`security-status ${connected ? 'connected' : 'disconnected'}`}>
              {connected ? 'Connected' : 'Disconnected'}
            </span>
          </div>

          <div className="security-detail">
            <label>Protocol Status:</label>
            <span className="security-value">{getProtocolStatusText()}</span>
          </div>

          <div className="security-detail">
            <label>Room ID:</label>
            <span className="security-value">{roomId}</span>
          </div>

          <div className="security-detail">
            <label>Participants:</label>
            <span className="security-value">{participants.join(', ') || 'None'}</span>
          </div>

          {encryptionDetails && (
            <>
              <div className="security-separator"></div>
              <div className="security-section-title">Cryptographic Details</div>

              <div className="security-detail">
                <label>Cipher:</label>
                <span className="security-value">{encryptionDetails.cipher}</span>
              </div>

              <div className="security-detail">
                <label>Key Derivation:</label>
                <span className="security-value">{encryptionDetails.keyDerivation}</span>
              </div>

              <div className="security-detail">
                <label>Key Exchange:</label>
                <span className="security-value">{encryptionDetails.keyExchange}</span>
              </div>

              <div className="security-detail">
                <label>Key Confirmation:</label>
                <span className="security-value">{encryptionDetails.keyConfirmation}</span>
              </div>

              <div className="security-detail">
                <label>AAD Binding:</label>
                <span className="security-value">{encryptionDetails.aadBinding}</span>
              </div>

              <div className="security-separator"></div>
              <div className="security-section-title">Session Statistics</div>

              <div className="security-detail">
                <label>Messages Sent:</label>
                <span className="security-value">{encryptionDetails.messagesSent}</span>
              </div>

              <div className="security-detail">
                <label>Messages Received:</label>
                <span className="security-value">{encryptionDetails.messagesReceived}</span>
              </div>
            </>
          )}
        </div>

        <div className="security-tooltip-footer">
          <small>End-to-end encrypted communication</small>
        </div>
      </div>
    </div>
  );
};

export default SecurityTooltip;
